/**
 * The answers to a question the assistant asks back: plain option chips, a confirmation with the planned change, or a
 * picker for a typed input. Each answer is emitted as the text to show and send, with a typed answer when there is one.
 */

import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { ClarifyingQuestion, Preset, QuestionAnswer, QuestionInput, ResourceCandidate } from './api/assistant.service';
import {
    DurationUnit,
    customRangeAnswer,
    durationMs,
    isValidAttribute,
    isValidText,
    lengthLabel,
    presetAnswer,
    toDateTimeLocal,
    toIsoDuration,
} from './assistant-question.util';

/** One answer: the message text the transcript shows, and the typed answer that travels with it. */
export interface QuestionReply {
    text: string;
    answer?: QuestionAnswer;
}

type InputOf<T extends QuestionInput['type']> = Extract<QuestionInput, { type: T }>;

const TEXT_PLACEHOLDERS: Record<InputOf<'TEXT'>['format'], string> = {
    HOSTNAME: 'web.example.internal',
    IP: '10.0.0.1',
    CIDR: '10.0.0.0/24',
    HOST_PORT: 'web.example.internal:443',
    PORT: '443',
    DOMAIN_NAME: 'shares.example.com',
    EMAIL: 'name@example.com',
};

@Component({
    selector: 'app-assistant-question',
    templateUrl: './assistant-question.component.html',
    styleUrls: ['./assistant-question.component.scss'],
    standalone: false,
})
export class AssistantQuestionComponent implements OnChanges {
    @Input({ required: true }) question!: ClarifyingQuestion;
    @Output() readonly answered = new EventEmitter<QuestionReply>();
    @ViewChild('discussionField') discussionField?: ElementRef<HTMLTextAreaElement>;
    @ViewChild('discussButton') discussButton?: ElementRef<HTMLButtonElement>;

    readonly placeholders = TEXT_PLACEHOLDERS;

    /** The custom range form under a time range question: open or not, and its two `datetime-local` values. */
    customRange: { from: string; to: string } | null = null;
    /** The resource picker: the filter text and the ids picked so far. */
    filter = '';
    selected = new Set<string>();
    /** The custom length form under a duration question. */
    customDuration: { amount: number | null; unit: DurationUnit } | null = null;
    numberValue: number | null = null;
    attributes: string[] = [];
    attributeDraft = '';
    textValue = '';
    /** A confirmation's reply form replaces its chips while open; the draft survives Back. */
    discussing = false;
    discussion = '';

    /** The question's input, narrowed to one type for the template, which cannot narrow a union across its blocks. */
    get timeRangeInput(): InputOf<'TIME_RANGE'> | null {
        return this.inputOf('TIME_RANGE');
    }

    get resourceInput(): InputOf<'RESOURCE'> | null {
        return this.inputOf('RESOURCE');
    }

    get durationInput(): InputOf<'DURATION'> | null {
        return this.inputOf('DURATION');
    }

    get numberInput(): InputOf<'NUMBER'> | null {
        return this.inputOf('NUMBER');
    }

    get attributesInput(): InputOf<'ATTRIBUTES'> | null {
        return this.inputOf('ATTRIBUTES');
    }

    get textInput(): InputOf<'TEXT'> | null {
        return this.inputOf('TEXT');
    }

    ngOnChanges(): void {
        const input = this.question.input;
        this.customRange = null;
        this.customDuration = null;
        this.filter = '';
        this.selected = new Set();
        this.numberValue = input?.type === 'NUMBER' ? (input.suggested ?? null) : null;
        this.attributes = input?.type === 'ATTRIBUTES' ? [...input.current] : [];
        this.attributeDraft = '';
        this.textValue = input?.type === 'TEXT' ? (input.suggested ?? '') : '';
        this.discussing = false;
        this.discussion = '';
    }

    /** An enum value as words: SERVICE_POLICY reads "Service policy". */
    words(value: string): string {
        const text = value.split('_').join(' ').toLowerCase();
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    // ---- plain options and confirmations

    /** A confirmation's options are fixed as "Yes, go ahead" then "No, cancel", so the first one confirms. */
    onOption(option: string, first: boolean): void {
        const confirmation = this.question.kind === 'CONFIRMATION';
        this.answered.emit({
            text: option,
            answer: confirmation ? { type: 'CONFIRMATION', confirmed: first } : undefined,
        });
    }

    /** Swap the chips for the reply form, focused after it renders. */
    onDiscuss(): void {
        this.discussing = true;
        setTimeout(() => this.discussionField?.nativeElement.focus());
    }

    /** Bring the chips back, focused on the chip that opened the form, so keyboard focus is not lost. */
    onEndDiscussion(): void {
        this.discussing = false;
        setTimeout(() => this.discussButton?.nativeElement.focus());
    }

    /** Enter sends the reply and Shift+Enter starts a new line, as in the chat input; Escape goes back to the chips. */
    onDiscussionKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.onEndDiscussion();
            return;
        }
        if (event.key !== 'Enter' || event.shiftKey) {
            return;
        }
        event.preventDefault();
        this.onSendDiscussion();
    }

    /** The reply goes as typed, marked as not confirmed so the assistant makes no change on it. */
    onSendDiscussion(): void {
        const text = this.discussion.trim();
        if (!text) {
            return;
        }
        this.answered.emit({ text, answer: { type: 'CONFIRMATION', confirmed: false, discuss: true } });
    }

    // ---- time range

    onPreset(preset: Preset): void {
        this.answered.emit({ text: preset.label, answer: presetAnswer(preset) ?? undefined });
    }

    /** Open the custom range form, filled with the first preset's range, or the last 24 hours. */
    onCustomRange(presets: Preset[]): void {
        const now = new Date();
        const suggested = presets.length ? presetAnswer(presets[0], now) : null;
        const from = suggested ? new Date(suggested.from) : new Date(now.getTime() - 24 * 60 * 60 * 1000);
        this.customRange = { from: toDateTimeLocal(from), to: toDateTimeLocal(now) };
    }

    /** The custom range as an answer, or null while it is not a valid range. */
    customAnswer(): QuestionAnswer | null {
        return this.customRange ? customRangeAnswer(this.customRange.from, this.customRange.to) : null;
    }

    onSendCustomRange(): void {
        const answer = this.customAnswer();
        if (answer?.type !== 'TIME_RANGE') {
            return;
        }
        this.answered.emit({
            text: `From ${new Date(answer.from).toLocaleString()} to ${new Date(answer.to).toLocaleString()}`,
            answer,
        });
    }

    // ---- resources

    visibleCandidates(input: InputOf<'RESOURCE'>): ResourceCandidate[] {
        const filter = this.filter.trim().toLowerCase();
        if (!filter) {
            return input.candidates;
        }
        return input.candidates.filter((candidate) =>
            `${candidate.name} ${candidate.detail ?? ''}`.toLowerCase().includes(filter)
        );
    }

    /** A single choice answers at once; a multiple choice collects picks until the user sends them. */
    onCandidate(input: InputOf<'RESOURCE'>, candidate: ResourceCandidate): void {
        if (!input.multiple) {
            this.answered.emit({
                text: candidate.name,
                answer: { type: 'RESOURCE', kind: input.kind, ids: [candidate.id] },
            });
            return;
        }
        const next = new Set(this.selected);
        if (next.has(candidate.id)) {
            next.delete(candidate.id);
        } else {
            next.add(candidate.id);
        }
        this.selected = next;
    }

    onSendCandidates(input: InputOf<'RESOURCE'>): void {
        const chosen = input.candidates.filter((candidate) => this.selected.has(candidate.id));
        if (chosen.length === 0) {
            return;
        }
        const text = chosen.map((candidate) => candidate.name).join(', ');
        this.answered.emit({
            text,
            answer: { type: 'RESOURCE', kind: input.kind, ids: chosen.map((candidate) => candidate.id) },
        });
    }

    // ---- duration

    onDurationPreset(preset: Preset): void {
        this.answered.emit({ text: preset.label, answer: { type: 'DURATION', duration: preset.duration } });
    }

    onCustomDuration(): void {
        this.customDuration = { amount: null, unit: 'H' };
    }

    /**
     * The custom length as an ISO-8601 duration, or null while it is not a whole number within the question's bounds.
     */
    customDurationValue(input: InputOf<'DURATION'>): string | null {
        if (!this.customDuration) {
            return null;
        }
        const iso = toIsoDuration(this.customDuration.amount, this.customDuration.unit);
        const length = iso ? durationMs(iso) : null;
        const min = durationMs(input.min) ?? 0;
        const max = durationMs(input.max) ?? Infinity;
        return iso && length !== null && length >= min && length <= max ? iso : null;
    }

    onSendCustomDuration(input: InputOf<'DURATION'>): void {
        const duration = this.customDurationValue(input);
        if (!duration || !this.customDuration?.amount) {
            return;
        }
        this.answered.emit({
            text: lengthLabel(this.customDuration.amount, this.customDuration.unit),
            answer: { type: 'DURATION', duration },
        });
    }

    // ---- number

    numberValid(input: InputOf<'NUMBER'>): boolean {
        const value = this.numberValue;
        return (
            typeof value === 'number' &&
            isFinite(value) &&
            (!input.integer || Number.isInteger(value)) &&
            (input.min === null || input.min === undefined || value >= input.min) &&
            (input.max === null || input.max === undefined || value <= input.max)
        );
    }

    onSendNumber(input: InputOf<'NUMBER'>): void {
        if (!this.numberValid(input) || this.numberValue === null) {
            return;
        }
        const text = input.unit ? `${this.numberValue} ${input.unit}` : `${this.numberValue}`;
        this.answered.emit({ text, answer: { type: 'NUMBER', value: this.numberValue } });
    }

    // ---- attributes

    attributeDraftValid(input: InputOf<'ATTRIBUTES'>): boolean {
        return isValidAttribute(input.mode, this.attributeDraft);
    }

    /** Enter or a comma adds the draft as a value; an invalid draft stays in the field. */
    onAttributeKeydown(event: KeyboardEvent, input: InputOf<'ATTRIBUTES'>): void {
        if (event.key !== 'Enter' && event.key !== ',') {
            return;
        }
        event.preventDefault();
        this.onAddAttribute(input);
    }

    onAddAttribute(input: InputOf<'ATTRIBUTES'>): void {
        const value = this.attributeDraft.trim();
        if (!this.attributeDraftValid(input)) {
            return;
        }
        if (!this.attributes.includes(value)) {
            this.attributes = [...this.attributes, value];
        }
        this.attributeDraft = '';
    }

    onRemoveAttribute(value: string): void {
        this.attributes = this.attributes.filter((attribute) => attribute !== value);
    }

    /**
     * A value still in the field is added first, so it is not lost; an invalid one blocks the send and keeps its error.
     */
    onSendAttributes(input: InputOf<'ATTRIBUTES'>): void {
        if (this.attributeDraft.trim()) {
            if (!this.attributeDraftValid(input)) {
                return;
            }
            this.onAddAttribute(input);
        }
        const label = input.mode === 'ROLES' ? 'Roles' : 'Attributes';
        const text = this.attributes.length ? `${label}: ${this.attributes.join(', ')}` : `No ${label.toLowerCase()}`;
        this.answered.emit({ text, answer: { type: 'ATTRIBUTES', mode: input.mode, values: this.attributes } });
    }

    // ---- text

    textValid(input: InputOf<'TEXT'>): boolean {
        return isValidText(input.format, this.textValue);
    }

    onSendText(input: InputOf<'TEXT'>): void {
        if (!this.textValid(input)) {
            return;
        }
        const value = this.textValue.trim();
        this.answered.emit({ text: value, answer: { type: 'TEXT', format: input.format, value } });
    }

    private inputOf<T extends QuestionInput['type']>(type: T): InputOf<T> | null {
        const input = this.question.input;
        return input?.type === type ? (input as InputOf<T>) : null;
    }
}
